import type * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import type * as models from './models'
import type { Plugin } from './plugin'
import type { State } from './state'

/**
 * - `all`: all entities in the collection
 * - `matching`: entities matching the current list params (ignoring pagination)
 * - `page`: entities on the current page
 * - `selected`: entities selected in the selection plugin (using default target)
 * - `current`: current entity in the currentEntity plugin (using default target)
 */
type StandardEntityListName =
  | 'all'
  | 'matching'
  | 'page'
  | 'selected'
  | 'current'

type EntityListInfo<TTypeParams extends ec.models.TypeParams.Constraint> =
  Partial<{
    // NOTE: ExactlyOneOf would be a more accurate return type than Partial,
    // but it causes weird type problems
    readonly request: ec.models.QueryRequest<TTypeParams>
    readonly ids: readonly ec.models.EntityId[]
    readonly entities: readonly TTypeParams['entity'][]
  }>

/**
 * Core actions for this {@link Plugin}
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig<
  TTypeParams extends ec.models.TypeParams.Constraint,
>(collection: ec.Collection<TTypeParams>) {
  return {
    /**
     * Retrieves the requested entity
     */
    download: store.actions.request({
      input: (
        /**
         * Either a {@link StandardEntityListName}, a {@link Source} of a
         * single entity or list of entities, or `undefined` to represent the
         * default (which is `selected` if there is a selection, or else
         * `matching`)
         */
        source:
          | undefined
          | StandardEntityListName
          | ExactlyOneOf<{
              readonly request: Source<ec.models.QueryRequest<TTypeParams>>
              readonly id: Source<ec.models.EntityId>
              readonly ids: Source<readonly ec.models.EntityId[]>
              readonly entity: Source<TTypeParams['entity']>
              readonly entities: Source<readonly TTypeParams['entity'][]>
            }>
          | rxjs.Observable<EntityListInfo<TTypeParams>>,
      ): WrapInObservableProducers<
        EntityListInfo<TTypeParams> & {
          readonly mix?: EntityListInfo<TTypeParams>
        }
      > => {
        const matchingRequest$ =
          collection.plugins.currentListPlugin.feature.selectors
            .queryRequestWithoutPagination.asObservable
        if (source === undefined) {
          // Default
          return {
            mix$: () =>
              collection.plugins.selectionPlugin.feature.selectors
                .getAllSelectedIds()
                .asObservable.pipe(
                  rxjs.switchMap(selectedIds =>
                    selectedIds.length
                      ? // If we have selected IDs, we use that
                        rxjs.of({ ids: selectedIds })
                      : // Otherwise, we use the request for the 'matching' list
                        matchingRequest$.pipe(
                          rxjs.map(request => ({ request })),
                        ),
                  ),
                ),
          }
        }
        if (typeof source === 'string') {
          // If the source is a standard selection, we use that selection
          switch (source) {
            case 'all':
              // For `all`, we use an empty query request
              return { request$: () => rxjs.of({}) }
            case 'matching':
              // For `matching`, we use the current list query request (without
              // pagination)
              return { request$: () => matchingRequest$ }
            case 'page':
              // For `page`, we use the current list query request (with
              // pagination)
              return {
                request$: () =>
                  collection.plugins.currentListPlugin.feature.selectors
                    .queryRequest.asObservable,
              }
            case 'selected':
              // For `selected`, we get all IDs from the selection plugin (the
              // coresponding entities will be requested for in the effect)
              return {
                ids$: () =>
                  collection.plugins.selectionPlugin.feature.selectors.getAllSelectedIds()
                    .asObservable,
              }
            case 'current':
              // For `current`, we get the current ID from the currentEntity
              // plugin (the entity will be requested for in the effect)
              return {
                ids$: () =>
                  collection.plugins.currentEntityPlugin.feature.selectors
                    .getId()
                    .asObservable.pipe(rxjs.map(id => (id ? [id] : []))),
              }
            default:
              throw TypeAsserter.assertIsNever(source)
          }
        }
        if (rxjs.isObservable(source)) {
          // Mix of different possible entity selections
          return { mix$: () => source }
        }
        // For entities, we will download these entities directly
        if (source.entities) {
          return { entities$: () => _sourceToObservable(source.entities) }
        }
        if (source.entity) {
          // If we have a single entity, we turn it into a list to simplify processing
          return {
            entities$: () =>
              _sourceToObservable(source.entity).pipe(
                rxjs.map(entity => [entity]),
              ),
          }
        }
        // For IDs, we will request for the entities that match the IDs in the
        // effect
        if (source.ids) {
          return { ids$: () => _sourceToObservable(source.ids) }
        }
        if (source.id) {
          // If we have a single ID, we turn it into a list to simplify
          // processing
          return {
            ids$: () =>
              _sourceToObservable(source.id).pipe(rxjs.map(id => [id])),
          }
        }
        // For a query request, we will request for the entities that match the
        // query request in the effect
        if (source.request) {
          return { request$: () => _sourceToObservable(source.request) }
        }
        // If none of the above, we can just use an empty list
        return { entities$: () => rxjs.of([]) }
      },
      output: store.actions.Data.asIs<models.Download>(),
      reducers: {
        cancellation: ({ state }) => ({ ...state, isDownloading: false }),
        error: ({ state }) => ({ ...state, isDownloading: false }),
        request: ({ state }) => ({ ...state, isDownloading: true }),
        success: ({ state }) => ({ ...state, isDownloading: false }),
      },
    }),
  } as const satisfies store.actions.Config.Constraint<State>
}

/**
 * Used to produce the given type
 */
type Source<T> =
  | T
  | store.selectors.BoundSelector<T>
  | rxjs.Observable<T>
  | Promise<T>

function _sourceToObservable<T>(source: Source<T>): rxjs.Observable<T> {
  if (source instanceof store.selectors.BoundSelector) {
    return source.asObservable
  }
  if (rxjs.isObservable(source)) {
    return source
  }
  if (source instanceof Promise) {
    return rxjs.from(source)
  }
  return rxjs.of(source)
}

type WrapInObservableProducers<T> = {
  // NOTE: this must be a callback function since actions get frozen, and once
  // they are frozen, the observables can get messed up
  [K in keyof T as `${Extract<K, string>}$`]: () => rxjs.Observable<
    NonNullable<T[K]>
  >
}
